import { useQueryClient } from '@tanstack/react-query';

import { useMutation, useQuery } from '@hooks/reactQuery';

import { auth } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setUpdatedUserRoles } from '@slices/auth';
import { setSnackbar } from '@slices/common';

import {
	blockUser,
	createUser,
	createUserAsPO,
	disableUserAsPO,
	enableUserAsPO,
	getDefaultSwapUsersDropdown,
	getSwapUsersDropdownPlatformOwner,
	getUsers,
	getUsersAsPo,
	removeOneUserAsPo,
	removeUsers,
	removeUsersAsPoUserTable,
	removeUsersOrganisationLevel,
	swapUsersRequest,
	unblockUser,
	updateUser,
	updateUserAsPO,
} from '@api/users';

import { extractQueryParams } from '@utils/common';
import { handleErrorFromMessage } from '@utils/handleRequestError';
import { hideDefaultRole } from '@utils/users';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { IItemsResponse } from '@interfaces/common/response';
import { IResponseError } from '@interfaces/errors/apiErrors';
import { ChangeStatusUserDTO } from '@interfaces/organisation/dto/organisationsDTO';
import { IDeleteUsersDTO, IGetUserDTO, IGetUsersDTO, IUserFormDTO } from '@interfaces/user/dto/userDTO';
import { IUserEntity } from '@interfaces/user/user';

import { EMenuActionsTypes, ESnackbarTypes } from '@enums/common';

import {
	CONTACT_PERSONS,
	DELETE_USER_AS_PO_ONE,
	FILTER_APPROVAL_USERS,
	ORGANISATION_PROFILE,
	ORGANIZATIONS_USERS_APPROVERS,
	SWAP_USER,
	SWAP_USERS_DROPDOWN,
	USERS,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IUseUsersParams extends IDeleteUsersDTO, IGetUsersDTO {
	closeDeleteConfirm?: () => void;
	closeModals?: () => void;
	isAdminExistInSelectedItems?: boolean;
	isEnabled?: boolean | null;
	organizationId?: string | null;
	page?: number;
	pageSize?: number;
	role?: string | null;
	sortDirection?: boolean | null;
	sortFieldName?: string | null;
	isPlatformOwner?: boolean;
	status?: string | null;
	organisationFilterId?: string | null;
	isManageUsersTable?: boolean;
	isOrganisationLevelDelete?: boolean;
	isOrganisationProfile?: boolean;
}

export const useUsers = (params?: IUseUsersParams) => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const {
		user,
		accessPermission: {
			platformOwnerAdministrator,
		},
	} = useAppSelector(auth);

	const {
		closeDeleteConfirm,
		closeModals,
		isAdminExistInSelectedItems,
		isEnabled,
		organizationId,
		page,
		pageSize,
		role,
		searchValue,
		sortDirection,
		sortFieldName,
		isPlatformOwner,
		organisationFilterId,
		status,
		isManageUsersTable,
		isOrganisationLevelDelete,
		isOrganisationProfile,
	} = params || {};

	const isManageUsersTablePO = platformOwnerAdministrator && isManageUsersTable;
	const isEnabledUsers = isManageUsersTablePO || !!organizationId;

	const {
		data,
		isLoading: isLoadingUsers,
		isFetching: isFetchingUsers,
	} = useQuery<IItemsResponse<IGetUserDTO>>({
			queryKey: [
				USERS,
				{
					page,
					pageSize,
					searchValue,
					sortDirection,
					sortFieldName,
					isEnabled,
					role,
					organizationId: isManageUsersTablePO ? organisationFilterId : organizationId,
					status,
				},
			],
			queryFn: extractQueryParams(isManageUsersTablePO ? getUsersAsPo : getUsers),
			select: (data) => ({
				...data,
				items: hideDefaultRole(data.items),
			}),
			enabled: [isEnabledUsers],
			refetchOnWindowFocus: false,
		},
	);

	const {
		data: swapUsersDropdownData,
		isLoading: isLoadingSwapUsersDropdown,
	} = useQuery({
			queryKey: [
				SWAP_USERS_DROPDOWN,
				organizationId,
			],
			queryFn: () => {
				if (isPlatformOwner && organizationId) {
					return getSwapUsersDropdownPlatformOwner(organizationId);
				}

				return getDefaultSwapUsersDropdown();
			},
			enabled: [isPlatformOwner !== undefined],
		},
	);

	const {
		mutate: handleDeleteUsers,
		isLoading: isLoadingDeleteUsers,
	} = useMutation<IGroupOperationWithActionDTO>({
		mutationKey: [
			USERS,
			searchValue,
			isEnabled,
			role,
			organizationId,
			status,
		],
		mutationFn: ({ ids, actionType }: IGroupOperationWithActionDTO) => {
			const removeRequest = isOrganisationLevelDelete
				? removeUsersOrganisationLevel
				: isManageUsersTablePO
					? removeUsersAsPoUserTable
					: removeUsers;

			const filter: IDeleteUsersDTO = {
				searchValue,
				isEnabled,
				status,
				role,
				organizationId: isManageUsersTablePO ? organisationFilterId : organizationId,
			};

			return removeRequest({
				filter: actionType === EMenuActionsTypes.allFiltered
					? filter
					: organizationId
						? { organizationId }
						: null,
				ids,
				isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
				...((isOrganisationLevelDelete || isManageUsersTablePO) ? { organizationId } : {}),
			});
		},
		onSuccess: () => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			dispatch(setSnackbar({
				message: isAdminExistInSelectedItems
					? responseMessages.removedUsersExceptAdmins
					: responseMessages.removedUsers,
				type: ESnackbarTypes.success,
			}));
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleDeleteUser,
		isLoading: isLoadingDeleteUser,
	} = useMutation<string>({
		mutationKey: [USERS],
		mutationFn: (id: string) => {
			const removeRequest = isOrganisationLevelDelete
				? removeUsersOrganisationLevel
				: isManageUsersTablePO
					? removeUsersAsPoUserTable
					: removeUsers;

			return removeRequest({
				ids: [id],
				isAll: false,
				filter: null,
				...((isOrganisationLevelDelete || isManageUsersTablePO) ? { organizationId } : {}),
			});
		},
		onSuccess: () => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			dispatch(setSnackbar({
				message: isAdminExistInSelectedItems
					? responseMessages.removedUsersExceptAdmins
					: responseMessages.removedUsers,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([USERS]),
				queryClient.invalidateQueries([ORGANISATION_PROFILE]),
				queryClient.invalidateQueries([CONTACT_PERSONS]),
				queryClient.invalidateQueries([FILTER_APPROVAL_USERS]),
				queryClient.invalidateQueries([ORGANIZATIONS_USERS_APPROVERS]),
			];
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleDeleteOneUserAsPo,
		isLoading: isLoadingDeleteOneUserAsPo,
	} = useMutation<string>({
		mutationKey: [DELETE_USER_AS_PO_ONE],
		mutationFn: (id: string) => {
			return removeOneUserAsPo({ id });
		},
		onSuccess: () => {
			if (closeDeleteConfirm) {
				closeDeleteConfirm();
			}

			dispatch(setSnackbar({
				message: isAdminExistInSelectedItems
					? responseMessages.removedUsersExceptAdmins
					: responseMessages.removedUsers,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([USERS]),
				queryClient.invalidateQueries([ORGANISATION_PROFILE]),
				queryClient.invalidateQueries([CONTACT_PERSONS]),
				queryClient.invalidateQueries([FILTER_APPROVAL_USERS]),
				queryClient.invalidateQueries([ORGANIZATIONS_USERS_APPROVERS]),
			];
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) {
				closeDeleteConfirm();
			}

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleUpdateUser,
		isLoading: isLoadingUpdateUser,
	} = useMutation<IUserFormDTO, IUserEntity>({
		mutationKey: [USERS],
		mutationFn: platformOwnerAdministrator && !isOrganisationProfile ? updateUserAsPO : updateUser,
		onSuccess: (data) => {
			if (closeModals) closeModals();

			dispatch(setSnackbar({
				message: responseMessages.updatedUser,
				type: ESnackbarTypes.success,
			}));

			if (user?.userId === data.id) {
				const roles = data.roles.map((role) => role.name);
				dispatch(setUpdatedUserRoles(roles));
			}

			return [
				queryClient.invalidateQueries([USERS]),
				queryClient.invalidateQueries([ORGANISATION_PROFILE]),
				queryClient.invalidateQueries([CONTACT_PERSONS]),
				queryClient.invalidateQueries([FILTER_APPROVAL_USERS]),
				queryClient.invalidateQueries([ORGANIZATIONS_USERS_APPROVERS]),
			];
		},
	});

	const {
		mutate: handleCreateUser,
		isLoading: isLoadingCreateUser,
	} = useMutation<IUserFormDTO, IUserEntity>({
		mutationKey: [USERS],
		mutationFn: platformOwnerAdministrator ? createUserAsPO : createUser,
		onSuccess: () => {
			if (closeModals) closeModals();

			dispatch(setSnackbar({
				message: responseMessages.createdUser,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([USERS]),
				queryClient.invalidateQueries([ORGANISATION_PROFILE]),
				queryClient.invalidateQueries([CONTACT_PERSONS]),
				queryClient.invalidateQueries([FILTER_APPROVAL_USERS]),
				queryClient.invalidateQueries([ORGANIZATIONS_USERS_APPROVERS]),
			];
		},
	});

	const {
		mutate: handleChangeStatusUser,
		isLoading: isChangeStatusUser,
	} = useMutation<ChangeStatusUserDTO>({
		mutationKey: [USERS, organizationId],
		mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => {
			if (isManageUsersTablePO) {
				const toggleStatusAsPO = isActive ? disableUserAsPO : enableUserAsPO;

				return toggleStatusAsPO({ id });
			}

			const toggleStatus = isActive ? blockUser : unblockUser;

			return toggleStatus({ id, organizationId });
		},
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.statusChanged,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([USERS]),
				queryClient.invalidateQueries([CONTACT_PERSONS]),
				queryClient.invalidateQueries([FILTER_APPROVAL_USERS]),
				queryClient.invalidateQueries([ORGANIZATIONS_USERS_APPROVERS]),
			];
		},
	});

	const {
		isLoading: isSwappingUserReference,
		mutate: swapUserReference,
	} = useMutation({
		mutationKey: [SWAP_USER],
		mutationFn: swapUsersRequest,
		onSuccess: () => {
			if (closeModals) closeModals();

			dispatch(setSnackbar({
				message: responseMessages.swapUser,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([USERS]),
				queryClient.invalidateQueries([CONTACT_PERSONS]),
				queryClient.invalidateQueries([FILTER_APPROVAL_USERS]),
				queryClient.invalidateQueries([ORGANIZATIONS_USERS_APPROVERS]),
			];
		},
	});

	return {
		swapUsersDropdownData: swapUsersDropdownData || [],
		isLoadingUsersDropdown: isLoadingSwapUsersDropdown,
		users: data?.items || [],
		countOfRecords: data?.count || 0,
		isLoadingTable: isLoadingDeleteUsers || isChangeStatusUser
			|| isLoadingDeleteUser || isLoadingUsers || isFetchingUsers,
		isLoadingDelete: isLoadingDeleteUsers || isLoadingDeleteUser || isLoadingDeleteOneUserAsPo,
		handleDeleteUsers,
		handleDeleteUser,
		handleUpdateUser,
		isLoadingUserForm: isLoadingUpdateUser || isLoadingCreateUser,
		handleChangeStatusUser,
		handleCreateUser,
		swapUserReference,
		isSwappingUserReference,
		handleDeleteOneUserAsPo,
	};
};
